/**
 * Combined Conversation Starters Hook
 *
 * Fetches unified conversation starters (personalized + trending) in a single API call
 *
 * Primary responsibilities:
 * - Make single unified API call for both personalized and trending starters
 * - Handle loading states from unified source
 * - Provide intelligent fallbacks
 * - Detect user location for trending content
 *
 * Dependencies:
 * - Unified conversation starters API
 */

import { useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { Tables } from '@/supabase/types'

interface UseCombinedConversationStartersProps {
  userId: string | undefined
  workspaceId: string | undefined
  selectedAssistant: Tables<'assistants'> | null
  region?: string
  language?: string
  enabled?: boolean
  includeTriending?: boolean
  maxStarters?: number
  forceRefresh?: boolean
}

interface ConversationStarter {
  display: string
  input: string
  trend?: any
  isTrending: boolean
}

interface CombinedStartersResult {
  starters: string[]
  structuredStarters: ConversationStarter[]
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  metadata: {
    personalizedCount: number
    trendingCount: number
    sources: string[]
    assistantName?: string
  }
}

const DEFAULT_COMBINED_STARTERS = [
  'Help me pick up where I left off',
  'What should I focus on next?',
  'Draft something for my current project',
  'Catch me up on what we were working on'
]

// Cache for unified conversation starters (session-based)
const unifiedCache = new Map<
  string,
  {
    starters: string[]
    structuredStarters: ConversationStarter[]
    metadata: any
    timestamp: number
  }
>()
const CACHE_DURATION = 30 * 1000 // 30 seconds for fresh starters

// --------------

/**
 * Generate cache key for unified conversation starters
 */
function getCacheKey(
  userId: string | undefined,
  workspaceId: string | undefined,
  assistantId: string | null,
  includeTrending: boolean,
  region: string,
  language: string,
  refreshKey?: string
): string {
  return `unified-${userId || 'unknown'}-${workspaceId || 'unknown'}-${assistantId || 'none'}-${includeTrending ? 'trending' : 'personal'}-${region}-${language}-${refreshKey || 'static'}`
}

// --------------

/**
 * Check if cache entry is still valid
 */
function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_DURATION
}

// --------------

/**
 * Get user's location for trending searches
 */
async function getUserLocation(): Promise<{ region: string; language: string }> {
  try {
    // First try browser geolocation API to get precise location
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      // For now, use browser language and fall back to country detection
      const browserLang = navigator.language || 'en-US'
      const [language, region] = browserLang.split('-')

      return {
        region: region?.toUpperCase() || 'US',
        language: language || 'en'
      }
    }

    // Fallback to IP-based detection
    const response = await fetch('/api/check-country')
    const data = await response.json()

    return {
      region: data.country || 'US',
      language: 'en'
    }
  } catch (error) {
    console.error('Failed to get user location:', error)
    return { region: 'US', language: 'en' }
  }
}

// --------------

/**
 * Fetch unified conversation starters from API
 */
async function fetchUnifiedConversationStarters(
  userId: string,
  workspaceId: string,
  selectedAssistant: Tables<'assistants'> | null,
  includeTrending: boolean,
  region: string,
  language: string,
  allTrending: boolean = false
): Promise<{ starters: string[]; structuredStarters: ConversationStarter[]; metadata: any }> {
  try {
    const response = await fetch('/api/generate-conversation-starters', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId,
        workspaceId,
        selectedAssistant,
        includeTrending: allTrending ? true : includeTrending, // Enable trending if allTrending
        allTrending,
        region,
        language
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    return {
      starters: data.conversationStarters || DEFAULT_COMBINED_STARTERS,
      structuredStarters:
        data.structuredStarters ||
        DEFAULT_COMBINED_STARTERS.map((s, i) => ({
          display: s,
          input: s,
          isTrending: false
        })),
      metadata: data.metadata || {
        personalizedCount: 4,
        trendingCount: 0,
        sources: ['default']
      }
    }
  } catch (error) {
    console.error('Error fetching unified conversation starters:', error)
    throw error
  }
}

// --------------

/**
 * Hook for managing unified conversation starters
 */
export function useCombinedConversationStarters({
  userId,
  workspaceId,
  selectedAssistant,
  region,
  language,
  enabled = true,
  includeTriending = false,
  allTrending = false, // Opt-in only — chat home keeps interest-only starters
  maxStarters = 8,
  forceRefresh = false
}: UseCombinedConversationStartersProps & { allTrending?: boolean }): CombinedStartersResult {
  // Always call useState unconditionally
  const [userLocation, setUserLocation] = useState<{ region: string; language: string }>({
    region: 'US',
    language: 'en'
  })
  const [starters, setStarters] = useState<string[]>(DEFAULT_COMBINED_STARTERS)
  const [structuredStarters, setStructuredStarters] = useState<ConversationStarter[]>(
    DEFAULT_COMBINED_STARTERS.map((s) => ({ display: s, input: s, isTrending: false }))
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [metadata, setMetadata] = useState({
    personalizedCount: 4,
    trendingCount: 0,
    sources: ['default'] as string[],
    assistantName: undefined as string | undefined
  })

  // Use ref to track mount time and generate unique session key
  const sessionKeyRef = useRef(`${Date.now()}-${Math.random()}`)

  // Always call useEffect unconditionally - move conditional logic inside
  useEffect(() => {
    const needsLocationDetection = !region || !language

    if (needsLocationDetection) {
      getUserLocation().then(setUserLocation)
    } else {
      setUserLocation({ region, language })
    }
  }, [region || '', language || '']) // Ensure dependencies are never undefined

  // Generate new session key when forceRefresh is true
  useEffect(() => {
    if (forceRefresh) {
      sessionKeyRef.current = `${Date.now()}-${Math.random()}`
    }
  }, [Boolean(forceRefresh)]) // Convert to boolean to ensure it's never undefined

  // Use provided values if available, otherwise use detected location
  const finalRegion = region || userLocation.region
  const finalLanguage = language || userLocation.language

  // Generate refresh key that includes unique session identifier
  const refreshKey = useMemo(() => {
    return `${userId || 'anon'}-${selectedAssistant?.id || 'none'}-${sessionKeyRef.current}`
  }, [userId || '', selectedAssistant?.id || '']) // Ensure dependencies are never undefined

  const cacheKey = getCacheKey(
    userId,
    workspaceId,
    selectedAssistant?.id || null,
    includeTriending,
    finalRegion,
    finalLanguage,
    refreshKey
  )

  // --------------

  const fetchStarters = useCallback(async () => {
    // For allTrending mode (public users), allow proceeding without userId/workspaceId
    if (!enabled) {
      const fallbackStructured = DEFAULT_COMBINED_STARTERS.map((s) => ({
        display: s,
        input: s,
        isTrending: false
      }))
      setStarters(DEFAULT_COMBINED_STARTERS)
      setStructuredStarters(fallbackStructured)
      setMetadata({
        personalizedCount: 4,
        trendingCount: 0,
        sources: ['default'],
        assistantName: undefined
      })
      return
    }

    // If allTrending mode, use placeholder IDs for API call
    const effectiveUserId = allTrending ? 'public' : userId
    const effectiveWorkspaceId = allTrending ? 'public' : workspaceId

    if (!effectiveUserId || !effectiveWorkspaceId) {
      const fallbackStructured = DEFAULT_COMBINED_STARTERS.map((s) => ({
        display: s,
        input: s,
        isTrending: false
      }))
      setStarters(DEFAULT_COMBINED_STARTERS)
      setStructuredStarters(fallbackStructured)
      setMetadata({
        personalizedCount: 4,
        trendingCount: 0,
        sources: ['default'],
        assistantName: undefined
      })
      return
    }

    // Check if assistant has predefined conversation starters - skip API call entirely
    if (
      selectedAssistant?.conversation_starters &&
      selectedAssistant.conversation_starters.length > 0
    ) {
      const assistantStarters = selectedAssistant.conversation_starters.slice(0, maxStarters)
      const structuredAssistantStarters = assistantStarters.map((starter: string) => ({
        display: starter,
        input: starter,
        isTrending: false
      }))

      setStarters(assistantStarters)
      setStructuredStarters(structuredAssistantStarters)
      setMetadata({
        personalizedCount: assistantStarters.length,
        trendingCount: 0,
        sources: ['assistant_predefined'],
        assistantName: selectedAssistant.name
      })
      setIsLoading(false)
      setError(null)
      return
    }

    // Skip cache check if forceRefresh is true
    if (!forceRefresh) {
      // Check cache first
      const cached = unifiedCache.get(cacheKey)
      if (cached && isCacheValid(cached.timestamp)) {
        setStarters(cached.starters)
        setStructuredStarters(cached.structuredStarters)
        setMetadata({
          ...cached.metadata,
          cached: true,
          cacheAge: Math.floor((Date.now() - cached.timestamp) / 1000)
        })
        return
      }
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await fetchUnifiedConversationStarters(
        effectiveUserId,
        effectiveWorkspaceId,
        selectedAssistant,
        includeTriending,
        finalRegion,
        finalLanguage,
        allTrending
      )

      // Cache the results (unless we're bypassing cache completely)
      if (!forceRefresh) {
        unifiedCache.set(cacheKey, {
          starters: result.starters,
          structuredStarters: result.structuredStarters,
          metadata: result.metadata,
          timestamp: Date.now()
        })
      }

      setStarters(result.starters)
      setStructuredStarters(result.structuredStarters)
      setMetadata(result.metadata)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch conversation starters'
      setError(errorMessage)
      // Fallback to defaults
      const fallbackStructured = DEFAULT_COMBINED_STARTERS.map((s) => ({
        display: s,
        input: s,
        isTrending: false
      }))
      setStarters(DEFAULT_COMBINED_STARTERS)
      setStructuredStarters(fallbackStructured)
      setMetadata({
        personalizedCount: 4,
        trendingCount: 0,
        sources: ['default'],
        assistantName: undefined
      })
    } finally {
      setIsLoading(false)
    }
  }, [
    userId,
    workspaceId,
    selectedAssistant,
    enabled,
    cacheKey,
    forceRefresh,
    includeTriending,
    finalRegion,
    finalLanguage,
    maxStarters
  ])

  // --------------

  // Fetch starters when dependencies change
  useEffect(() => {
    fetchStarters()
  }, [fetchStarters])

  // --------------

  // Always call useCallback unconditionally
  const refetch = useCallback(async () => {
    await fetchStarters()
  }, [fetchStarters])

  return {
    starters,
    structuredStarters,
    isLoading,
    error,
    refetch,
    metadata
  }
}
